import { useEffect, useState } from 'react';
import axios from 'axios';
import { Warning, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FONCTIONS } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDT(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; }
}

/**
 * RP85 — « Envois d'e-mails en pause » (ancien « Mode préparation » de l'onglet
 * Accès & Invitations), déplacé dans l'écran unique « Équipe & accès ». Admin seulement.
 */
export default function EnvoisEnPause() {
  const [actif, setActif] = useState(false);
  const [depuis, setDepuis] = useState(null);
  const [enAttente, setEnAttente] = useState(0);
  const [chargement, setChargement] = useState(false);
  const [confirmer, setConfirmer] = useState(false);

  const charger = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/mode-preparation`, { withCredentials: true });
      setActif(data.mode_preparation);
      setDepuis(data.mode_preparation_since);
      setEnAttente(data.emails_pending || 0);
    } catch { /* silencieux */ }
  };

  useEffect(() => { charger(); }, []);

  const appliquer = async (checked) => {
    setChargement(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/mode-preparation`, { active: checked }, { withCredentials: true });
      setActif(data.mode_preparation);
      setEnAttente(data.emails_pending || 0);
      toast.success(checked ? "Envois d'e-mails en pause" : "Les envois d'e-mails reprennent");
      await charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de changer l'état des envois");
    } finally {
      setChargement(false);
      setConfirmer(false);
    }
  };

  const basculer = (checked) => {
    if (!checked && actif) { setConfirmer(true); return; }
    appliquer(checked);
  };

  return (
    <div className="border border-[#E0E0DB] bg-white p-4 sm:p-5" data-testid="mode-preparation-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-[#2C2C2C] mb-1">{FONCTIONS.envoisEnPause}</h3>
          <p className="text-sm text-[#6B6B6B]">
            {actif
              ? <>Aucun e-mail ne part. Préparez les comptes et les chantiers, puis reprenez les envois quand tout est prêt.</>
              : <>Les e-mails partent normalement (invitations, comptes rendus, appels d'offres, signatures).</>}
          </p>
          {actif && (
            <p className="text-xs text-[#7A3B0A] mt-2 flex items-center gap-1" data-testid="mode-prep-banner">
              <Warning size={14} weight="fill" />
              En pause{depuis ? ` depuis le ${fmtDT(depuis)}` : ''} — {enAttente} e-mail{enAttente > 1 ? 's' : ''} retenu{enAttente > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Switch checked={actif} onCheckedChange={basculer} disabled={chargement} data-testid="toggle-mode-prep" />
      </div>

      <Dialog open={confirmer} onOpenChange={(o) => !o && setConfirmer(false)}>
        <DialogContent className="max-w-lg" data-testid="confirm-disable-mode-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              <Warning size={22} className="text-[#C4773B]" />
              Reprendre les envois d'e-mails
            </DialogTitle>
            <DialogDescription>
              Les prochains événements enverront les e-mails automatiques habituels.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[#FDF6EC] p-3 border-l-4 border-[#C4773B] text-sm text-[#7A3B0A]">
            {enAttente > 0 ? (
              <p className="text-xs">
                Les {enAttente} e-mail{enAttente > 1 ? 's' : ''} retenu{enAttente > 1 ? 's' : ''} pendant la pause (notifications de test) seront <strong>supprimés sans être envoyés</strong>.
                Les invitations partent avec « Envoyer les invitations » ou « Renvoyer l'invitation ».
              </p>
            ) : (
              <p className="text-xs">Aucun e-mail retenu.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setConfirmer(false)} className="rounded-none">Annuler</Button>
            <Button onClick={() => appliquer(false)} disabled={chargement} className="bg-[#C4773B] hover:bg-[#A5623A] rounded-none gap-2" data-testid="confirm-disable-mode-btn">
              {chargement ? <><CircleNotch size={14} className="animate-spin" /> Reprise…</> : 'Reprendre les envois'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
